import React, { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import {
  disconnectPlugin as apiDisconnectPlugin,
  createOrUpdatePluginInstance,
} from "@fuse/api/pluginApi"
// Import our custom hook and API functions
import usePluginDetails from "@fuse/hooks/usePluginDetails"
import PluginRenderer from "@fuse/sharedComponents/PluginRenderer"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { useRecoilValue } from "recoil"

import { UserRole } from "@fuse/recoil/pluginStudio/types"
import { userState } from "@fuse/recoil/signup"

import { getEventDetails } from "@api/firebaseWrappedApis"

interface EventData {
  id: string
  eventName: string
  eventDescription?: string
  timing?: {
    eventStartTime: string
    eventEndTime: string
  }
  location?: {
    eventLocation?: string
    latitude?: number
    longitude?: number
  }
  initiatorUid?: string
  [key: string]: any
}

// Add this utility function to the top of the file, after imports
const extractErrorDetails = (err: unknown): string => {
  if (err instanceof Error) {
    return `${err.name}: ${err.message}${err.stack ? `\n${err.stack}` : ""}`
  }
  return String(err)
}

const PluginDetailPage: React.FC = () => {
  const { pluginId } = useParams<{ pluginId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const eventId = searchParams.get("event_id")
  const user = useRecoilValue(userState)

  // Use our custom hook to get plugin details
  const {
    plugin,
    instance: pluginInstance,
    isConnected,
    userRole,
    isLoading: pluginLoading,
    hasError: pluginError,
    errorMessage: pluginErrorMessage,
    refresh: refreshPluginDetails,
  } = usePluginDetails(pluginId || "", eventId || "")
  if (pluginError) {
    console.log("pluginError", pluginError)
  }
  if (pluginLoading) {
    console.log("pluginLoading", pluginLoading)
  }
  if (plugin) {
    console.log("plugin", plugin)
  }
  if (pluginErrorMessage) {
    console.log("pluginErrorMessage", pluginErrorMessage)
  }

  const [event, setEvent] = useState<EventData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adminComponent, setAdminComponent] = useState<React.ReactNode | null>(
    null,
  )
  const [saving, setSaving] = useState(false)
  const [configuration, setConfiguration] = useState<any>({})
  const [pluginEnabled, setPluginEnabled] = useState<boolean>(true)

  // Update state from plugin details when they load
  useEffect(() => {
    if (plugin && !pluginLoading) {
      // Skip for built-in integrations
      if (
        pluginId &&
        [
          "questionnaires",
          "rsvp_notes",
          "shared_playlist",
          "shared_album",
        ].includes(pluginId)
      ) {
        setError(
          "Built-in integrations are not yet configurable through this interface.",
        )
        setLoading(false)
        return
      }

      // If we have an instance, set its configuration
      if (pluginInstance) {
        setConfiguration(pluginInstance.configuration || {})
        setPluginEnabled(pluginInstance.enabled)
      }
    }

    if (pluginError) {
      setError(pluginErrorMessage || "Error loading plugin details")
    }
  }, [
    plugin,
    pluginInstance,
    pluginLoading,
    pluginError,
    pluginErrorMessage,
    pluginId,
  ])

  // Load event data
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return

      try {
        setLoading(true)
        console.log(`Fetching event data for event: ${eventId}`)

        // Use the getEventDetails API function instead of direct Firestore access
        const response = await getEventDetails({
          eventId,
          uid: user?.uid || "",
        })

        if (response.success) {
          const eventData = response.data
          setEvent({
            id: eventId,
            eventName: eventData.eventName || eventData.eventDocData?.eventName,
            eventDescription:
              eventData.eventDescription ||
              eventData.eventDocData?.eventDescription,
            timing: eventData.timing || eventData.eventDocData?.timing,
            location: eventData.location || eventData.eventDocData?.location,
            initiatorUid:
              eventData.initiatorUid || eventData.eventDocData?.initiatorUid,
            ...eventData,
          })
          console.log(`Event data loaded successfully`)
        } else {
          console.error(
            `Failed to load event: ${response.message || "Unknown error"}`,
          )
          setError(`Event not found or you don't have permission to access it.`)
        }
      } catch (err) {
        console.error("Error loading event:", err)
        setError("Failed to load event data")
      } finally {
        setLoading(false)
      }
    }

    fetchEvent()
  }, [eventId, user?.uid])

  // Loading the admin component for the plugin
  useEffect(() => {
    if (
      pluginLoading ||
      !plugin ||
      !plugin.code?.transpiledFrontendHostConfigCodeWeb
    ) {
      setAdminComponent(
        <div className="p-4 bg-[#3A3D64] rounded-lg">
          <span className="text-white text-sm">Loading admin component...</span>
        </div>,
      )
      return
    }

    try {
      // Use our standardized PluginRenderer for admin view too
      setAdminComponent(
        <PluginRenderer
          plugin={plugin}
          config={configuration}
          eventId={eventId || ""}
          userId={user?.uid || ""}
          userRole={UserRole.HOST_CONFIG_VIEW}
          isLoggedIn={!!user}
          eventMetadata={{
            name: event?.eventName || "Demo Event",
            description: event?.eventDescription || "This is a demo event",
            timing: event?.timing || {
              eventStartTime: new Date().toISOString(),
              eventEndTime: new Date(Date.now() + 86400000).toISOString(),
            },
            location: event?.location || {
              eventLocation: "Demo Location",
            },
            flyerImage: event?.flyerImage || "",
          }}
        />,
      )
    } catch (err) {
      console.error("Error rendering admin component:", err)
      setError(
        `Error rendering admin component: ${err instanceof Error ? err.message : String(err)}`,
      )
    }
  }, [
    plugin,
    pluginLoading,
    configuration,
    eventId,
    user?.uid,
    event,
    userRole,
  ])

  // Save plugin instance and configuration
  const handleSave = async () => {
    if (!plugin || !eventId || !user?.uid) {
      showErrorToast("Cannot save plugin configuration")
      return
    }

    setSaving(true)

    try {
      const response = await createOrUpdatePluginInstance({
        eventId,
        pluginId: plugin.id,
        configuration,
        enabled: pluginEnabled,
      })

      if (response.success) {
        showSuccessToast("Plugin configuration saved successfully")
        // Refresh plugin details to update the instance
        refreshPluginDetails()
      } else {
        throw new Error(response.error || "Failed to save plugin configuration")
      }
    } catch (err) {
      console.error("Error saving plugin instance:", err)
      showErrorToast("Failed to save plugin configuration")
    } finally {
      setSaving(false)
    }
  }

  // Toggle plugin enabled state
  const togglePluginEnabled = async () => {
    if (!plugin || !eventId || !user?.uid) {
      showErrorToast("Cannot toggle plugin state")
      return
    }

    try {
      const newEnabledState = !pluginEnabled
      setPluginEnabled(newEnabledState)

      const response = await createOrUpdatePluginInstance({
        eventId,
        pluginId: plugin.id,
        configuration,
        enabled: newEnabledState,
      })

      if (response.success) {
        showSuccessToast(
          `Plugin ${newEnabledState ? "enabled" : "disabled"} successfully`,
        )
        refreshPluginDetails()
      } else {
        throw new Error(response.error || "Failed to update plugin state")
      }
    } catch (err) {
      console.error("Error toggling plugin state:", err)
      // Revert state on error
      setPluginEnabled(pluginEnabled)
      showErrorToast("Failed to update plugin state")
    }
  }

  // Disconnect plugin from event
  const disconnectPlugin = async () => {
    if (!pluginInstance?.id) {
      showErrorToast("No plugin instance to disconnect")
      return
    }

    try {
      const response = await apiDisconnectPlugin({
        instanceId: pluginInstance.id,
      })

      if (response.success) {
        // Reset local state
        setConfiguration({})
        setPluginEnabled(false)
        showSuccessToast("Plugin disconnected from event successfully")
        // Refresh plugin details to update
        refreshPluginDetails()
      } else {
        throw new Error(response.error || "Failed to disconnect plugin")
      }
    } catch (err) {
      console.error("Error disconnecting plugin:", err)
      showErrorToast("Failed to disconnect plugin")
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  // Add this function to render the admin component with proper error handling
  function renderAdminComponent() {
    // If there's already an error, show it
    if (error && error.includes("admin component")) {
      return (
        <div className="bg-red-800 bg-opacity-30 border border-red-500 rounded-lg p-4 mb-6">
          <h3 className="font-bold mb-2">Error with Admin Component</h3>
          <p className="text-sm">{error}</p>
          <p className="mt-2 text-sm">
            You can still save basic configuration settings.
          </p>
        </div>
      )
    }

    // If loading or no admin component yet
    if (!adminComponent) {
      return (
        <div className="bg-yellow-800 bg-opacity-30 border border-yellow-500 rounded-lg p-4 mb-6">
          <p>Loading configuration interface...</p>
        </div>
      )
    }

    // Render the admin component with error boundary
    try {
      return (
        <div className="mb-6 pb-6 border-b border-[#8B8D97] border-opacity-20">
          <ErrorBoundary
            fallback={(errorMsg) => (
              <div className="bg-red-800 bg-opacity-30 border border-red-500 rounded-lg p-4">
                <h3 className="font-bold mb-2">
                  Error rendering admin component
                </h3>
                <p className="text-sm whitespace-pre-wrap">{errorMsg}</p>
                <p className="mt-2 text-sm">
                  You can still save basic configuration settings.
                </p>
              </div>
            )}
          >
            {adminComponent}
          </ErrorBoundary>
        </div>
      )
    } catch (err) {
      console.error("Error in renderAdminComponent:", err)
      return (
        <div className="bg-red-800 bg-opacity-30 border border-red-500 rounded-lg p-4 mb-6">
          <h3 className="font-bold mb-2">Unexpected Error</h3>
          <p className="text-sm whitespace-pre-wrap">
            {extractErrorDetails(err)}
          </p>
          <p className="mt-2 text-sm">
            You can still save basic configuration settings.
          </p>
        </div>
      )
    }
  }

  if (loading || pluginLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#2F3152] text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  if (error || pluginError) {
    return (
      <div className="bg-[#2F3152] text-white min-h-screen p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleBack}
            className="mb-6 px-4 py-2 bg-[#5A5E83] bg-opacity-20 rounded-lg text-[#C2C3CC]"
          >
            ← Back
          </button>

          <div className="bg-red-800 bg-opacity-30 border border-red-500 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Error</h2>
            <p>{error || pluginErrorMessage}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!plugin || !event) {
    return (
      <div className="bg-[#2F3152] text-white min-h-screen p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleBack}
            className="mb-6 px-4 py-2 bg-[#5A5E83] bg-opacity-20 rounded-lg text-[#C2C3CC]"
          >
            ← Back
          </button>

          <div className="bg-yellow-800 bg-opacity-30 border border-yellow-500 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Missing Data</h2>
            <p>
              {!plugin
                ? "Plugin information could not be loaded."
                : "Event information could not be loaded."}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[#2F3152] text-white min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={handleBack}
          className="mb-6 px-4 py-2 bg-[#5A5E83] bg-opacity-20 rounded-lg text-[#C2C3CC]"
        >
          ← Back
        </button>

        {/* Plugin Details Section - Always display this if plugin exists */}
        {plugin && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#C2C3CC] mb-2">
              {plugin.manifest.name}
            </h1>
            <p className="text-[#8B8D97] mb-4">{plugin.manifest.description}</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <h2 className="text-sm text-[#8B8D97] uppercase mb-1">
                  Version
                </h2>
                <p className="text-[#C2C3CC]">{plugin.manifest.version}</p>
              </div>
              <div>
                <h2 className="text-sm text-[#8B8D97] uppercase mb-1">
                  Author
                </h2>
                <p className="text-[#C2C3CC]">{plugin.manifest.author}</p>
              </div>
            </div>

            {event && (
              <div className="mb-4">
                <h2 className="text-sm text-[#8B8D97] uppercase mb-1">Event</h2>
                <p className="text-[#C2C3CC]">{event.eventName}</p>
              </div>
            )}

            {plugin.manifest.searchTags &&
              plugin.manifest.searchTags.length > 0 && (
                <div className="mb-4">
                  <h2 className="text-sm text-[#8B8D97] uppercase mb-1">
                    Tags
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {plugin.manifest.searchTags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-[#5A5E83] bg-opacity-20 rounded-full text-xs text-[#C2C3CC]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Configuration Section - Will show a message if there's a problem */}
        <div className="bg-[#5A5E83] bg-opacity-10 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-[#C2C3CC] mb-4">
            Configuration
          </h2>

          {/* Render the admin component with robust error handling */}
          {renderAdminComponent()}

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-6 py-3 rounded-lg text-white font-medium ${
                saving
                  ? "bg-blue-700 opacity-70 cursor-not-allowed"
                  : "bg-blue-700 hover:bg-blue-600"
              }`}
            >
              {saving ? "Saving..." : "Save Configuration"}
            </button>
          </div>
        </div>

        {/* Plugin Status Section */}
        <div className="bg-[#5A5E83] bg-opacity-10 rounded-lg p-6">
          <h2 className="text-xl font-bold text-[#C2C3CC] mb-4">
            Plugin Status
          </h2>

          <div className="flex items-center justify-between mb-4">
            <span className="text-[#C2C3CC]">
              {pluginInstance
                ? "Connected to this event"
                : "Not yet connected to this event"}
            </span>
            <div className="flex items-center space-x-3">
              <span
                className={`px-3 py-1 rounded-full text-xs ${
                  pluginEnabled
                    ? "bg-green-900 bg-opacity-30 text-green-400"
                    : "bg-yellow-900 bg-opacity-30 text-yellow-400"
                }`}
              >
                {pluginEnabled ? "Enabled" : "Disabled"}
              </span>

              <button
                onClick={togglePluginEnabled}
                className={`px-3 py-1 rounded-md text-xs transition-colors ${
                  pluginEnabled
                    ? "bg-yellow-700 hover:bg-yellow-600 text-white"
                    : "bg-green-700 hover:bg-green-600 text-white"
                }`}
              >
                {pluginEnabled ? "Disable" : "Enable"}
              </button>

              {pluginInstance && (
                <button
                  onClick={disconnectPlugin}
                  className="px-3 py-1 rounded-md text-xs bg-red-700 hover:bg-red-600 text-white transition-colors"
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>

          <p className="text-[#8B8D97] text-sm">
            This plugin will be available to event hosts and guests once
            configured and enabled.
          </p>
        </div>
      </div>
    </div>
  )
}

// Error boundary to catch runtime errors in plugin rendering
interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback: (errorMessage: string) => React.ReactNode
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, errorMessage: "" }
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI
    console.error("Error caught in ErrorBoundary:", error)
    return {
      hasError: true,
      errorMessage: error.message || "Unknown error",
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to console for debugging
    console.error("Error in admin component:", error)
    console.error("Error details:", errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback(this.state.errorMessage)
    }

    // Wrap the children in an error-catching div
    return (
      <div
        onError={(e) => {
          // Prevent the default browser behavior to show our custom error
          e.preventDefault()
          console.error("DOM error caught:", e)
          this.setState({
            hasError: true,
            errorMessage:
              e instanceof ErrorEvent ? e.message : "Unknown DOM error",
          })
        }}
      >
        {this.props.children}
      </div>
    )
  }
}

export default PluginDetailPage
